import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Search, MapPin, Clock, IndianRupee, Bookmark, BookmarkCheck, Filter, Calendar, Users, BadgeCheck } from 'lucide-react';

const OPPORTUNITY_TYPE_LABELS = {
  gig: 'GIG',
  job_role: 'JOB ROLE',
  contract: 'CONTRACT',
  rfp: 'RFP'
};

const OPPORTUNITY_TYPE_COLORS = {
  gig: 'bg-purple-100 text-purple-700',
  job_role: 'bg-blue-100 text-blue-700',
  contract: 'bg-green-100 text-green-700',
  rfp: 'bg-orange-100 text-orange-700'
};

export default function BrowseOpportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({
    method: '',
    sector: '',
    location_type: '',
    min_budget: '',
    max_budget: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [methods, setMethods] = useState([]);
  const [sectors, setSectors] = useState([]);
  
  useEffect(() => {
    // Fetch constants
    const fetchConstants = async () => {
      try {
        const [methodsRes, sectorsRes] = await Promise.all([
          api.get('/constants/methods'),
          api.get('/constants/sectors')
        ]);
        setMethods(methodsRes.data.methods || []);
        setSectors(sectorsRes.data.sectors || []);
      } catch (error) {
        console.error('Error fetching constants:', error);
      }
    };
    fetchConstants();
  }, []);
  
  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (activeTab !== 'all') params.append('opportunity_type', activeTab);
      if (filters.method) params.append('method', filters.method);
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.location_type) params.append('location_type', filters.location_type);
      if (filters.min_budget) params.append('min_budget', filters.min_budget);
      if (filters.max_budget) params.append('max_budget', filters.max_budget);
      
      const [oppsRes, savedRes] = await Promise.all([
        api.get(`/opportunities?${params.toString()}`),
        api.get('/saved-opportunities')
      ]);
      
      setOpportunities(oppsRes.data.opportunities || []);
      setTotal(oppsRes.data.total || 0);
      setSavedIds(new Set(savedRes.data.map(s => s.opportunity_id)));
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchOpportunities();
  }, [activeTab]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchOpportunities();
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const applyFilters = () => {
    fetchOpportunities();
    setShowFilters(false);
  };
  
  const clearFilters = () => {
    setFilters({
      method: '',
      sector: '',
      location_type: '',
      min_budget: '',
      max_budget: ''
    });
    setSearch('');
  };
  
  const toggleSave = async (oppId) => {
    try {
      if (savedIds.has(oppId)) {
        await api.delete(`/opportunities/${oppId}/save`);
        setSavedIds(prev => {
          const next = new Set(prev);
          next.delete(oppId);
          return next;
        });
      } else {
        await api.post(`/opportunities/${oppId}/save`);
        setSavedIds(prev => new Set([...prev, oppId]));
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };
  
  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'gig', label: 'Gigs' },
    { key: 'job_role', label: 'Job Roles' },
    { key: 'contract', label: 'Contracts' },
    { key: 'rfp', label: 'RFPs' }
  ];
  
const formatBudget = (min, max) => {
    if (!min && !max) return null;
    const formatNum = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${(n/1000).toFixed(0)}K`;
    if (min && max) return `${formatNum(min)} - ${formatNum(max)}`;
    if (max) return `Up to ${formatNum(max)}`;
    return `From ${formatNum(min)}`;
  };
  
  return (
    <DashboardLayout title="Browse Opportunities">
      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.key
                ? 'bg-[#FF6B35] text-white'
                : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
            <input
              type="text"
              data-testid="search-input"
              placeholder="Search by title, company, or skills..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-neutral-200 bg-white focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none"
            />
          </div>
          <button
            type="button"
            data-testid="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 border ${showFilters ? 'border-[#FF6B35] bg-[#FFF0EB]' : 'border-neutral-200 bg-white'} flex items-center gap-2 transition-colors`}
          >
            <Filter size={20} />
            <span className="hidden sm:inline">Filters</span>
          </button>
          <button
            type="submit"
            data-testid="search-submit"
            className="btn-researcher px-6 py-3"
          >
            Search
          </button>
        </div>
      </form>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-neutral-200 p-6 mb-6 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ fontFamily: 'Outfit' }}>Filters</h3>
            <button onClick={clearFilters} className="text-sm text-neutral-500 hover:text-neutral-700">
              Clear all
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Method</label>
              <select
                data-testid="filter-method"
                value={filters.method}
                onChange={(e) => handleFilterChange('method', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 bg-white text-sm"
              >
                <option value="">All Methods</option>
                {methods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sector</label>
              <select
                data-testid="filter-sector"
                value={filters.sector}
                onChange={(e) => handleFilterChange('sector', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 bg-white text-sm"
              >
                <option value="">All Sectors</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
              <select
                data-testid="filter-location"
                value={filters.location_type}
                onChange={(e) => handleFilterChange('location_type', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 bg-white text-sm"
              >
                <option value="">All</option>
                <option value="remote">Remote</option>
                <option value="onsite">On-site</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Min Budget</label>
              <input
                type="number"
                data-testid="filter-min-budget"
                placeholder="₹"
                value={filters.min_budget}
                onChange={(e) => handleFilterChange('min_budget', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Max Budget</label>
              <input
                type="number"
                data-testid="filter-max-budget"
                placeholder="₹"
                value={filters.max_budget}
                onChange={(e) => handleFilterChange('max_budget', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              data-testid="apply-filters"
              onClick={applyFilters}
              className="btn-researcher px-6 py-2"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
      
      {/* Results */}
      <div className="text-sm text-neutral-500 mb-4">
        {total} opportunities found
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
        </div>
      ) : opportunities.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <p className="text-neutral-500 mb-2">No opportunities found</p>
          <p className="text-sm text-neutral-400">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {opportunities.map((opp) => (
            <div
              key={opp.opportunity_id}
              data-testid={`opportunity-card-${opp.opportunity_id}`}
              className="bg-white border border-neutral-200 p-5 card-researcher hover-lift"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 ${OPPORTUNITY_TYPE_COLORS[opp.opportunity_type] || 'bg-neutral-100 text-neutral-600'}`}>
                    {OPPORTUNITY_TYPE_LABELS[opp.opportunity_type] || opp.opportunity_type?.toUpperCase()}
                  </span>
                  {opp.is_verified && (
                    <BadgeCheck className="text-blue-500" size={16} />
                  )}
                </div>
                <button
                  data-testid={`save-btn-${opp.opportunity_id}`}
                  onClick={() => toggleSave(opp.opportunity_id)}
                  className={`p-1.5 rounded-sm transition-colors ${
                    savedIds.has(opp.opportunity_id)
                      ? 'text-[#FF6B35] bg-[#FFF0EB]'
                      : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  {savedIds.has(opp.opportunity_id) ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
                </button>
              </div>
              
              <Link to={`/opportunity/${opp.opportunity_id}`}>
                <h3 className="font-semibold text-neutral-900 mb-1 hover:text-[#FF6B35] transition-colors line-clamp-1">
                  {opp.title}
                </h3>
              </Link>
              
              <p className="text-sm text-neutral-500 mb-2">
                {opp.company_name || opp.client?.name || 'Anonymous'}
              </p>
              
              <div className="flex flex-wrap gap-3 text-xs text-neutral-500 mb-3">
                {opp.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={12} />
                    {opp.location}
                  </span>
                )}
                {opp.timeline && (
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {opp.timeline}
                  </span>
                )}
                {formatBudget(opp.budget_min, opp.budget_max) && (
                  <span className="flex items-center gap-1">
                    <IndianRupee size={12} />
                    {formatBudget(opp.budget_min, opp.budget_max)}
                  </span>
                )}
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {opp.method && (
                    <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600">
                      {opp.method}
                    </span>
                  )}
                  {opp.sector && (
                    <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600">
                      {opp.sector}
                    </span>
                  )}
                </div>
                <span className="text-xs text-neutral-400 flex items-center gap-1">
                  <Users size={12} />
                  {opp.application_count || 0} applied
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
